import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  SelectChangeEvent,
  FormHelperText,
} from "@mui/material";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import axios, { AxiosError } from "axios";
import { useCart } from "../../../hooks/shareContext";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface FormErrors {
  phoneNumber?: string;
  subject?: string;
  message?: string;
  appointmentId?: string;
  trackType?: string;
  languageId?: string;
  websiteId?: string;
  animationId?: string;
  checkbox?: boolean | string;
  selectedDate?: string;
}

interface smsData {
  smsData: smsContent[];
  selectedIcon: (icon: string) => void;
}

interface smsContent {
  subject: string;
  message: string;
  type: string;
}

const countryData = intlTelInput.getCountryData();
const countryOptions = countryData
  .filter(
    (country) =>
      !(country.dialCode === "1" && country.iso2 !== "us") &&
      !(country.dialCode === "44" && country.iso2 !== "gb")
  )
  .map((country) => ({
    code: `+${country.dialCode}`,
    flag: country.iso2,
    name: country.name,
  }));

const SmsForm: React.FC<smsData> = ({ smsData, selectedIcon }) => {
  const { selectAnim, myVideosAnim } = useCart();
  const [appointmentID, setAppointmentID] = useState<string>("");
  const [selectedMessage, setSelectedMessage] = useState<string>("");
  const [consent, setConsent] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [textFieldMessage, setTextFieldMessage] = useState<string>("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(String);
  const [countryCode, setCountryCode] = useState("+1");
  const [countryFlag, setCountryFlag] = useState("us");

  const [totalTextCount, setTotalTextCount] = useState(Number);
  const { t } = useTranslation();
  const [userEnteredMessageCount, setUserEnteredMessageCount] =
    useState(totalTextCount);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({}); // for Form errors object

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setCurrentDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  // setting the phone number
  useEffect(() => {
    if (phoneInputRef.current) {
      const iti = intlTelInput(phoneInputRef.current, {
        initialCountry: "us",
        separateDialCode: true,
      });

      const handleInputChange = () => {
        const formattedNumber = iti.getNumber();
        setPhoneNumber(formattedNumber || "");
      };

      phoneInputRef.current.addEventListener("input", handleInputChange);

      return () => {
        phoneInputRef.current?.removeEventListener("input", handleInputChange);
        iti.destroy();
      };
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("countryCode")) {
      const fetchCountryCode = async () => {
        try {
          const response = await axios.get("https://ipapi.co/json/");
          const res = response.data.country_calling_code;
          localStorage.setItem("countryCode", res);
          const countryCode = localStorage.getItem("countryCode");
          const country = countryOptions.find(
            (option) => option.code === countryCode
          );
          if (country) {
            setCountryCode(country.code);
            setCountryFlag(country.flag);
          }
        } catch (error) {
          console.error("Error fetching country code:", error);
        }
      };
      fetchCountryCode();
    }
  }, []);

  // setting the total text count
  useEffect(() => {
    if (countryCode === "+1") {
      setTotalTextCount(131);
    } else {
      setTotalTextCount(131);
    }
  }, [countryCode]);

  useEffect(() => {
    const isValid =
      phoneNumber.trim() !== "" &&
      selectedMessage.trim() !== "" &&
      textFieldMessage.trim() !== "" &&
      consent;
    setIsFormValid(isValid);
  }, [phoneNumber, selectedMessage, textFieldMessage, selectedDate, consent]);

  const handleCountryChange = (e: SelectChangeEvent<string>) => {
    const selectedCountry = countryOptions.find(
      (option) => option.code === e.target.value
    );
    if (selectedCountry) {
      setCountryCode(selectedCountry.code);
      setCountryFlag(selectedCountry.flag);
    }
  };

  const handleDateChange = (e: SelectChangeEvent<string>) => {
    setSelectedDate(e.target.value);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const selectedValue = JSON.parse(e.target.value); // Parse the stringified object
    setSelectedMessage(selectedValue.subject);
    setTextFieldMessage(selectedValue.message);
    setUserEnteredMessageCount(totalTextCount - selectedValue.message.length);
    if (countryCode === "+1") {
      setTotalTextCount(1400);
    } else {
      setTotalTextCount(160);
    }
  };
  const handleTextFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (value.length <= totalTextCount) {
      setTextFieldMessage(value); // Assuming you have a setter function for textFieldMessage
      setUserEnteredMessageCount(totalTextCount - value.length);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPhoneNumber(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;
    if (
      !/[\d]/.test(key) &&
      key !== "Backspace" &&
      key !== "ArrowLeft" &&
      key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  };

  // Function to reset all fields
  const resetForm = () => {
    setPhoneNumber("");
    setAppointmentID("");
    setConsent(false);
    setSelectedMessage("");
    setTextFieldMessage("");
    setSelectedDate("");
    setIsFormValid(false);
  };

  const validateForm = () => {
    const errors: FormErrors = {};

    if (!phoneNumber.trim()) {
      errors.phoneNumber = "Please enter a Phone Number.";
    }
    if (!selectedMessage.trim()) {
      errors.subject = "Please enter a Subject.";
    }
    if (!textFieldMessage.trim()) {
      errors.message = "Please enter a Message.";
    }
    if (!consent) {
      errors.checkbox = "Please make sure to give consent.";
    }
    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    return isValid;
  };

  const handleSendSMS = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    const SMSData = {
      phoneNumber: countryCode + phoneNumber,
      subject: selectedMessage,
      message: textFieldMessage,
      appointmentId: appointmentID,
      trackType: "share_sms",
      languageId: 50,
      websiteId: 1,
      animationId: selectAnim.join(","),
      myVideos: myVideosAnim.join(","),
    };


    const animationShareSMS = async (SMSData: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "share/sms-send",
          SMSData
        );

        if (response.status >= 200 && response.status <= 300) {
          setTimeout(() => {
            toast.success(response.data.message, {
              position: "top-right",
              autoClose: 1000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          }, 2000);

          selectedIcon("");
          // Reset all fields after submission
          resetForm();
        } else {
        }
      } catch (error) {
        const err = error as AxiosError<{ message: string }>;
        toast.error(err.response?.data?.message, {
          autoClose: 1000,
        });
      }
    };
    animationShareSMS(SMSData);
  };

  return (
    <>
      {/* <ToastContainer /> */}
      <form id="smsForm">
        <Grid container spacing={2} sx={{ padding: "10px" }}>
          <Grid item xs={12}></Grid>
          <Grid
            item
            xs={12}
            sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
          >
            <Select
              value={countryCode}
              onChange={handleCountryChange}
              displayEmpty
              required
              renderValue={(selected) => {
                const selectedCountry = countryOptions.find(
                  (option) => option.code === selected
                );
                return selectedCountry ? (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={`https://flagcdn.com/16x12/${selectedCountry.flag}.png`}
                      alt={countryFlag}
                      style={{ width: 30, height: 22, marginRight: 8 }}
                    />
                    {selectedCountry.code}
                  </div>
                ) : null;
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: {
                  style: {
                    maxHeight: 400, // Decrease the height of the list view
                  },
                },
              }}
              sx={{ flex: 0.5 }}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  <img
                    src={`https://flagcdn.com/16x12/${option.flag}.png`}
                    alt={`${option.name} flag`}
                    style={{ width: 20, height: 15, marginRight: 8 }}
                  />
                  {option.name} ({option.code})
                </MenuItem>
              ))}
            </Select>
            <TextField
              label={t("mobile_number")}
              variant="outlined"
              placeholder={t("enter_mobile_number")}
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyDown={handleKeyDown}
              sx={{ flex: 2.5 }}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber}
              required
              type="tel" // Allow only tel input
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("appointment_id")}
              variant="outlined"
              value={appointmentID}
              onChange={(e) => setAppointmentID(e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Select
              sx={{
                ...(formErrors.subject && { border: "1px solid #d32f2f" }),
              }}
              fullWidth
              value={selectedMessage}
              onChange={handleSelectChange}
              displayEmpty
              required
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>-- Select a message --</em>;
                }
                return selected;
              }}
              MenuProps={MenuProps}
              inputProps={{ "aria-label": "Without label" }}
            >
              <MenuItem disabled value="">
                <em>-- Select a message --</em>
              </MenuItem>
              {smsData.map((item, index) => (
                <MenuItem
                  key={index}
                  value={JSON.stringify({
                    message: item.message,
                    subject: item.subject,
                  })}
                >
                  {item.subject}
                </MenuItem>
              ))}
            </Select>
            {formErrors.subject && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                {formErrors.subject}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              value={textFieldMessage}
              minRows={5}
              maxRows={10}
              error={!!formErrors.message}
              helperText={formErrors.message}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                if (e.target.value.length <= 200) {
                  handleTextFieldChange(e);
                }
              }}
            />
            {textFieldMessage && (
              <Typography
                variant="subtitle1"
                sx={{ fontSize: "14px", textAlign: "right" }}
              >
                {userEnteredMessageCount}/{totalTextCount}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography
              sx={{ fontSize: "11px", borderBottom: "1px solid lightgrey" }}
            >
              {t("share_sms_text_message")}
            </Typography>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex" }}>
            <Checkbox
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              sx={{ padding: 1 }}
            />

            <Typography sx={{ display: "flex", fontSize: "13px" }}>
              {t("share_receipient_text_meassge")}

              {/* The recipient of this text message has given verbal or written
              consent to receive text messages regarding their vehicle repair. */}
            </Typography>
          </Grid>
          {formErrors.checkbox && (
            <FormHelperText sx={{ color: "#d32f2f", marginLeft: "15px" }}>
              {formErrors.checkbox}
            </FormHelperText>
          )}
          <Grid item xs={12} gap={2} sx={{ display: "flex" }}>
            <Typography variant="body2">
              <strong>{t("opt_in _date")}</strong>
            </Typography>

            <input
              type="date"
              id="myDate"
              name="myDate"
              placeholder="mm/dd/yyyy"
              value={selectedDate || currentDate}
              max={currentDate}
              onChange={handleDateChange}
              onFocus={(e) => e.target.showPicker && e.target.showPicker()} // triggers calendar on focus
              style={{
                fontSize: "15px",
                padding: "1px",
                width: "130px",
              }}
            />

            {formErrors.selectedDate && (
              <FormHelperText sx={{ color: "#d32f2f", marginLeft: "15px" }}>
                {formErrors.selectedDate}
              </FormHelperText>
            )}
          </Grid>

          <Grid
            item
            xs={12}
            sm="auto"
            sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}
          >
            <Button
              id="sendSMS"
              onClick={handleSendSMS}
              disabled={!isFormValid}
              sx={{
                backgroundColor: isFormValid ? "primary.main" : "#ddd",
                textTransform: "none",
                cursor: isFormValid ? "pointer" : "not-allowed",
                color: "white",
              }}
            >
              {t("send_sms")}
            </Button>

            <Button variant="outlined">{t("cancel")}</Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default SmsForm;
